import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import {
  getApiAdmincompaniesGetCollectionQueryKey,
  useApiAdmincompaniesIdGet,
  useApiAdmincompaniesIdDelete
} from '@r/api/generated/client.ts';
import type {
  CompanyHistoryCompanyReadModel,
  OrderCompanyReadModel,
  ValuePerMonthCompanyReadModel
} from '@r/api/generated/client.schemas.ts';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useHistoryColumns } from '@r/components/history/history-columns.tsx';
import CompanyTarget from '@admin/components/company/logic/target/company-target.tsx';
import {
  useCompanyTargetColumns,
  useCompanyOrdersColumns
} from '@admin/components/company/logic/target/hooks/use-company-columns.tsx';
import { Button } from '@ui/button.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import pointsTranslation from '@r/translations/points-translation.ts';
import { StatBrick } from '@admin/components/stat-brick.tsx';
import {
  History,
  Target,
  ChartLineIcon,
  CoinsIcon,
  StarIcon,
  Plus,
  PackageIcon,
  WalletIcon,
  ShoppingCartIcon,
  PiggyBankIcon,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import {
  type FormattedDataForRendering,
  BarChartBloc
} from '@r/components/kpi/points/bar-chart-bloc.tsx';
import { useContext, useMemo, useState } from 'react';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { getBarFillFromValue } from '@r/utils/chart-utils.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { CompanyPerformanceMovementsTable } from './components/company-performance-movements-table.tsx';
import { CompanyPointsMovementsTable } from './components/company-points-movements-table.tsx';
import { EmailHistoryTable } from '@admin/components/emails/email-history-table.tsx';
import { HistoryCard } from '@admin/components/history-card.tsx';
import { useUser } from '@r/api/auth.tsx';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@r/components/ui/dialog.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isStepLogicWorkspace, isTypologyConversion } from '@r/utils/workspace-utils.ts';
import { AddPerformanceDialog } from '@admin/components/forms/performances/add-performance-dialog.tsx';
import { AddPointDialog } from '@admin/components/forms/points/add-point-dialog.tsx';
import { CompanyProfileHeader } from '@admin/components/company/company-profile-header.tsx';
import { CompanyDetailsGrid } from '@admin/components/company/company-details-grid.tsx';
import { CompanySkeleton } from '@admin/components/company/company-skeleton.tsx';
import { CompanyStepsBloc } from '@admin/components/company/company-steps-bloc.tsx';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type CompanyHistory = CompanyHistoryCompanyReadModel;
type PointsPerMonth = ValuePerMonthCompanyReadModel;
//

export const Company = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: companyId } = getRouteApi('/auth-layout/companies/$id').useParams();
  const [perfView, setPerfView] = useState<'revenue' | 'points'>('revenue');
  const [isAddPerformanceDialogOpen, setIsAddPerformanceDialogOpen] = useState(false);
  const [isAddPointDialogOpen, setIsAddPointDialogOpen] = useState(false);
  const [isCompanyInfoModalOpen, setIsCompanyInfoModalOpen] = useState(false);
  const [isResponsibleModalOpen, setIsResponsibleModalOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<'warning' | 'confirm' | null>(null);
  const [companyNameInput, setCompanyNameInput] = useState('');
  const user = useUser();
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const { workspace } = UseWorkspace(user.data);
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const isStepWorkspace = isStepLogicWorkspace(workspace);
  const showTypology =
    isTypologyConversion(workspaceConfigContext) && (workspace?.typologies?.length ?? 0) > 1;
  const isTargetPointLogic =
    workspace?.currentLogicWorkspace?.logic.type === LogicLogicReadModelType.TARGET_POINT;
  const canAddPoints =
    activeWorkspace?.roles?.includes('ROLE_ADMIN') ||
    activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN') ||
    false;

  let formattedPointsPerMonth: FormattedDataForRendering[] = [];
  let totalPeriodPoints: number | null = null;
  let formattedPerfsPerMonth: FormattedDataForRendering[] = [];
  let totalPeriodPerfs: number | null = null;

  const {
    data: company,
    isLoading,
    isSuccess,
    isError,
    error,
    errorUpdatedAt,
    refetch: refetchCompany
  } = useApiAdmincompaniesIdGet(companyId);
  useOrvalQueryErrorToast({ isError, error, errorUpdatedAt }, t('toast.error.label'));

  const registeredAt = company?.registeredAt;

  const deleteMutation = useApiAdmincompaniesIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.company.profile.toast.delete.success'));
        queryClient.invalidateQueries({ queryKey: getApiAdmincompaniesGetCollectionQueryKey() });
        navigate({ to: '/companies' });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.company.profile.toast.delete.error'));
      }
    }
  });

  const closeDeleteModal = () => {
    setDeleteStep(null);
    setCompanyNameInput('');
  };

  const companyHistory = useMemo(() => {
    if (!company?.companyHistory) return [];

    const registeredAtEntry = registeredAt
      ? [
          {
            property: 'registeredAt',
            newValue: format(new Date(registeredAt), 'dd/MM/yyyy')
          } as CompanyHistory
        ]
      : [];

    return [...registeredAtEntry, ...company.companyHistory];
  }, [company?.companyHistory, registeredAt]);

  const { table: companyHistoryTable } = useDataTable({
    data: companyHistory,
    columns: useHistoryColumns<CompanyHistory>(),
    enableAdvancedFilter: true,
    manualPaginate: false,
    manualSort: false,
    manualFilter: false
  });

  const { table: companyTargetTable } = useDataTable({
    data: company?.targetStatuses || [],
    columns: useCompanyTargetColumns(isTargetPointLogic),
    enableAdvancedFilter: true,
    manualPaginate: false,
    manualSort: false,
    manualFilter: false
  });

  const { table: companyOrders } = useDataTable({
    data: company?.orders || [],
    columns: useCompanyOrdersColumns(),
    enableAdvancedFilter: true,
    manualPaginate: false,
    manualSort: false,
    manualFilter: false
  });

  if (isLoading) {
    return (
      <div className="space-y-10">
        <h1 className="text-4xl font-bold">{t('admin.company.profile.title')}</h1>
        <CompanySkeleton />
      </div>
    );
  }

  if (isSuccess) {
    const isCompanyBlocked = company.status === 'company.status.blocked';
    const isCompanyRegistered = company.status === 'company.status.registered';
    const hasPointsStats =
      Boolean(company.balance ?? 0) ||
      Boolean(company.acquiredPoints ?? 0) ||
      Boolean(company.spentPoints ?? 0);
    totalPeriodPoints = company?.pointsPerMonth
      ? company.pointsPerMonth.reduce((acc, item) => acc + Number(item.value), 0)
      : 0;

    const pointsPerMonth = company?.pointsPerMonth ?? [];
    const monthsCount = pointsPerMonth.length;

    const averagePoints =
      monthsCount > 0
        ? Math.round(
            pointsPerMonth.reduce((acc, item) => acc + Number(item.value), 0) / monthsCount
          )
        : 0;

    const bestPerformance = pointsPerMonth.reduce(
      (max, item) => (Number(item.value) > max ? Number(item.value) : max),
      0
    );

    const bestMonthObj = pointsPerMonth.reduce(
      (best, item) =>
        Number(item.value) > (best ? Number(best.value) : Number.NEGATIVE_INFINITY) ? item : best,
      null as null | (typeof pointsPerMonth)[0]
    );

    const bestMonthLabel = bestMonthObj
      ? `${format(new Date(bestMonthObj.year, bestMonthObj.month - 1), 'MMMM yyyy', { locale: fr })}`
      : '-';

    const processPointsPerMonthResults = (
      totalPointsPerMonth: PointsPerMonth[]
    ): FormattedDataForRendering[] => {
      return totalPointsPerMonth.map((item) => {
        const monthName = format(new Date(item.year, item.month - 1), 'MMMM', {
          locale: fr
        });

        const formatedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        const totalPoints = Number(item.value);

        return {
          date: `${formatedMonthName} ${item.year}`,
          data: totalPoints,
          fill: getBarFillFromValue(totalPoints)
        };
      });
    };

    formattedPointsPerMonth = processPointsPerMonthResults(company.pointsPerMonth);

    totalPeriodPerfs = company?.perfsPerMonth
      ? company.perfsPerMonth.reduce((acc, item) => acc + Number(item.value), 0)
      : 0;

    const processPerfsPerMonthResults = (
      totalPerfsPerMonth: PointsPerMonth[]
    ): FormattedDataForRendering[] => {
      return totalPerfsPerMonth.map((item) => {
        const monthName = format(new Date(item.year, item.month - 1), 'MMMM', {
          locale: fr
        });

        const formatedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        const totalPerfs = Number(item.value);

        return {
          date: `${formatedMonthName} ${item.year}`,
          data: totalPerfs,
          fill: getBarFillFromValue(totalPerfs)
        };
      });
    };

    formattedPerfsPerMonth = processPerfsPerMonthResults(company.perfsPerMonth || []);

    const perfsPerMonth = company?.perfsPerMonth ?? [];
    const perfsMonthsCount = perfsPerMonth.length;

    const averagePerfs =
      perfsMonthsCount > 0
        ? Math.round(
            perfsPerMonth.reduce((acc, item) => acc + Number(item.value), 0) / perfsMonthsCount
          )
        : 0;

    const bestPerformancePerfs = perfsPerMonth.reduce(
      (max, item) => (Number(item.value) > max ? Number(item.value) : max),
      0
    );

    const bestMonthPerfsObj = perfsPerMonth.reduce(
      (best, item) =>
        Number(item.value) > (best ? Number(best.value) : Number.NEGATIVE_INFINITY) ? item : best,
      null as null | (typeof perfsPerMonth)[0]
    );

    const bestMonthPerfsLabel = bestMonthPerfsObj
      ? `${format(new Date(bestMonthPerfsObj.year, bestMonthPerfsObj.month - 1), 'MMMM yyyy', { locale: fr })}`
      : '-';

    return (
      <div className="space-y-5">
        <CompanyProfileHeader
          company={company}
          isCompanyBlocked={isCompanyBlocked}
          isCompanyRegistered={isCompanyRegistered}
          canAddPoints={canAddPoints}
          isStepWorkspace={isStepWorkspace}
          showTypology={showTypology}
        />

        <CompanyDetailsGrid
          company={company}
          modalState={{
            isCompanyInfoOpen: isCompanyInfoModalOpen,
            isResponsibleOpen: isResponsibleModalOpen
          }}
          handlers={{
            setCompanyInfoOpen: setIsCompanyInfoModalOpen,
            setResponsibleOpen: setIsResponsibleModalOpen
          }}
        />

        {company.targetStatuses && company.targetStatuses?.length > 0 ? (
          <Card className="w-full">
            <CardContent className="flex flex-col p-10 space-y-5 w-full">
              <div className="flex items-center space-x-2.5">
                <Target />
                <h2 className="text-xl font-bold">{t('admin.company.target.title')}</h2>
              </div>

              {company.targetStatuses.map((targetStatus) => (
                <CompanyTarget
                  key={targetStatus.id}
                  targetStatus={
                    targetStatus as unknown as Parameters<typeof CompanyTarget>[0]['targetStatus']
                  }
                  isTargetPointLogic={isTargetPointLogic}
                  currencySymbol={company.country?.currencySymbol}
                />
              ))}

              <DataTable table={companyTargetTable} />
            </CardContent>
          </Card>
        ) : null}

        {company.pointsPerMonth && company.pointsPerMonth.length > 0 && (
          <Card className="flex flex-col">
            <CardHeader className="p-10">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center space-x-2 text-xl font-author-complete font-regular">
                  <ChartLineIcon />
                  <p className="text-xl font-bold">{t('company.perf.section')}</p>
                </CardTitle>

                <div className="flex gap-2 p-1 rounded-full bg-muted">
                  <Button
                    variant={perfView === 'revenue' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPerfView('revenue')}
                    className={`rounded-full ${perfView === 'revenue' ? 'bg-dark text-white hover:bg-dark/90' : ''}`}
                  >
                    {t('company.perf.switch.revenue')}
                  </Button>
                  <Button
                    variant={perfView === 'points' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPerfView('points')}
                    className={`rounded-full ${perfView === 'points' ? 'bg-dark text-white hover:bg-dark/90' : ''}`}
                  >
                    {t('company.perf.switch.points')}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex p-10 pt-0">
              {perfView === 'revenue' && (
                <div className="flex flex-col gap-6 w-full">
                  {formattedPerfsPerMonth.length > 0 && (
                    <Card className="flex flex-col justify-center p-6 space-y-6 w-full h-full rounded-xl">
                      <StatBrick
                        title={t('company.revenue.total')}
                        icon={<ChartLineIcon />}
                        value={totalPeriodPerfs}
                        valueSuffix="€"
                        valueClassName="text-4xl"
                        className="w-fit"
                        cardMode={false}
                        hasValidData={isSuccess}
                        loading={isLoading || isError}
                      />

                      <BarChartBloc
                        classname="h-[200px]"
                        formattedDataPerMonth={formattedPerfsPerMonth}
                        dataType={'monetary'}
                        loading={isLoading || isError}
                      />

                      <div className="text-sm font-light text-center text-muted-foreground">
                        {t('company.revenue.chart.info')}
                      </div>
                    </Card>
                  )}

                  {formattedPerfsPerMonth.length > 0 && (
                    <div className="grid grid-cols-12 gap-6">
                      <div className="flex col-span-12 xl:col-span-6">
                        <StatBrick
                          title={t('company.revenue.avg')}
                          icon={<ChartLineIcon />}
                          value={averagePerfs}
                          valueSuffix="€"
                          valueClassName="text-4xl"
                          className="rounded-xl"
                          hasValidData={isSuccess}
                          loading={isLoading || isError}
                        />
                      </div>

                      <div className="flex col-span-12 xl:col-span-3">
                        <StatBrick
                          title={t('company.revenue.best')}
                          icon={<StarIcon />}
                          value={bestPerformancePerfs}
                          valueSuffix="€"
                          className="rounded-xl"
                          hasValidData={isSuccess}
                          loading={isLoading || isError}
                        />
                      </div>

                      <div className="flex col-span-12 xl:col-span-3">
                        <StatBrick
                          title={t('company.revenue.best.month')}
                          icon={<StarIcon />}
                          value={bestMonthPerfsLabel}
                          className="rounded-xl"
                          hasValidData={isSuccess}
                          loading={isLoading || isError}
                        />
                      </div>
                    </div>
                  )}

                  {company?.uniqueCode ? (
                    <CompanyPerformanceMovementsTable companyUniqueCode={company.uniqueCode} />
                  ) : null}
                </div>
              )}

              {perfView === 'points' && isSuccess && formattedPointsPerMonth.length > 0 && (
                <div className="flex flex-col gap-6 w-full">
                  <Card className="flex flex-col justify-center p-6 space-y-6 w-full h-full rounded-xl">
                    {isSuccess && (
                      <StatBrick
                        title={t('company.points.total')}
                        icon={<CoinsIcon />}
                        value={totalPeriodPoints}
                        valueSuffix={pointsTranslation(t, totalPeriodPoints)}
                        valueClassName="text-4xl"
                        className="w-fit"
                        cardMode={false}
                        hasValidData={isSuccess}
                        loading={isLoading || isError}
                      />
                    )}

                    <BarChartBloc
                      classname="h-[200px]"
                      formattedDataPerMonth={formattedPointsPerMonth}
                      dataType={'point'}
                      loading={isLoading || isError}
                    />

                    <div className="text-sm font-light text-center text-muted-foreground">
                      {t('company.points.chart.info')}
                    </div>
                  </Card>

                  <div className="grid grid-cols-12 gap-6">
                    <div className="flex col-span-12 xl:col-span-6">
                      <StatBrick
                        title={t('company.points.avg')}
                        icon={<CoinsIcon />}
                        value={averagePoints}
                        valueSuffix={pointsTranslation(t, averagePoints)}
                        valueClassName="text-4xl"
                        className="rounded-xl"
                        hasValidData={isSuccess}
                        loading={isLoading || isError}
                      />
                    </div>

                    <div className="flex col-span-12 xl:col-span-3">
                      <StatBrick
                        title={t('company.points.best')}
                        icon={<StarIcon />}
                        value={bestPerformance}
                        valueSuffix={pointsTranslation(t, bestPerformance)}
                        className="rounded-xl"
                        hasValidData={isSuccess}
                        loading={isLoading || isError}
                      />
                    </div>

                    <div className="flex col-span-12 xl:col-span-3">
                      <StatBrick
                        title={t('company.points.best.month')}
                        icon={<StarIcon />}
                        value={bestMonthLabel}
                        className="rounded-xl"
                        hasValidData={isSuccess}
                        loading={isLoading || isError}
                      />
                    </div>
                  </div>

                  {company?.uniqueCode ? (
                    <CompanyPointsMovementsTable companyUniqueCode={company.uniqueCode} />
                  ) : null}
                </div>
              )}
            </CardContent>

            <CardFooter className="flex gap-4 p-10 pt-0 w-full">
              {perfView === 'revenue' ? (
                <Button
                  rounded="full"
                  className="w-full"
                  onClick={() => setIsAddPerformanceDialogOpen(true)}
                >
                  <Plus className="mr-2 w-4 h-4" />
                  {t('common.add.performance')}
                </Button>
              ) : (
                <Button
                  rounded="full"
                  className="w-full"
                  onClick={() => setIsAddPointDialogOpen(true)}
                >
                  <Plus className="mr-2 w-4 h-4" />
                  {t('common.add.point')}
                </Button>
              )}
            </CardFooter>
          </Card>
        )}
        {company ? (
          <>
            <AddPerformanceDialog
              isOpen={isAddPerformanceDialogOpen}
              onOpenChange={setIsAddPerformanceDialogOpen}
              onSuccess={refetchCompany}
              fixedCompany={{
                id: company.id,
                uniqueCode: company.uniqueCode,
                name: company.name
              }}
            />
            <AddPointDialog
              isOpen={isAddPointDialogOpen}
              onOpenChange={setIsAddPointDialogOpen}
              onSuccess={refetchCompany}
              fixedCompany={{
                id: company.id,
                uniqueCode: company.uniqueCode,
                name: company.name
              }}
            />
          </>
        ) : null}
        {hasPointsStats ? (
          <Card className="flex flex-col">
            <CardHeader className="p-10">
              <CardTitle className="flex items-center space-x-2 text-xl font-author-complete font-regular">
                <PiggyBankIcon />
                <p className="text-xl font-bold">{t('company.point.section')}</p>
              </CardTitle>
            </CardHeader>

            <CardContent className="flex p-10 pt-0">
              <div className="flex flex-col gap-6 w-full">
                <div className="grid grid-cols-12 gap-6">
                  <div className="flex col-span-12 xl:col-span-4">
                    <StatBrick
                      title={t('company.available.points')}
                      icon={<WalletIcon />}
                      value={company.balance}
                      valueSuffix={pointsTranslation(t, company?.balance)}
                      hasValidData={isSuccess}
                    />
                  </div>

                  <div className="flex col-span-12 xl:col-span-4">
                    <StatBrick
                      title={t('company.acquired.points')}
                      icon={<CoinsIcon />}
                      value={company.acquiredPoints}
                      valueSuffix={pointsTranslation(t, company?.acquiredPoints)}
                      hasValidData={isSuccess}
                    />
                  </div>

                  <div className="flex col-span-12 xl:col-span-4">
                    <StatBrick
                      title={t('company.spent.points')}
                      icon={<ShoppingCartIcon />}
                      value={company?.spentPoints}
                      valueSuffix={pointsTranslation(t, company?.spentPoints)}
                      hasValidData={isSuccess}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {isStepWorkspace && (
          // balance = solde actif (points non expirés). acquiredPoints = cumul total historique.
          // La progression aux paliers se base sur le solde actif, pas le cumul.
          // logicStepHistory = paliers de la logique courante uniquement (filtrés par le backend).
          <CompanyStepsBloc
            lastStepReached={company.lastStepReached ?? null}
            acquiredPoints={company.balance}
            logicStepHistory={company.logicStepHistory}
            workspaceSteps={workspace?.currentLogicWorkspace?.steps}
          />
        )}

        {company.orders && company.orders?.length > 0 ? (
          <Card className="w-full">
            <CardContent className="flex flex-col p-10 space-y-5 w-full">
              <div className="flex items-center space-x-2.5">
                <PackageIcon />
                <h2 className="text-xl font-bold">{t('common.orders')}</h2>
              </div>

              <DataTable
                table={companyOrders}
                onRowClick={(row: OrderCompanyReadModel) => {
                  if (!row.id) return;
                  navigate({
                    to: '/shops/orders/$id',
                    params: { id: String(row.id) }
                  });
                }}
              />

              <Button rounded="full" className="w-full">
                <LinkProtected className="flex gap-2 items-center" to="/shops/orders">
                  <PackageIcon />
                  {t('common.see.all')}
                </LinkProtected>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        <HistoryCard
          title={t('admin.emails.title')}
          description={t('admin.company.emails.description')}
        >
          <EmailHistoryTable
            key={company.uniqueCode}
            source={{ type: 'company', uniqueCode: company.uniqueCode }}
          />
        </HistoryCard>

        {company.companyHistory.length > 0 && (
          <Card className="w-full">
            <CardContent className="flex flex-col p-10 space-y-5 w-full">
              <div className="space-y-2.5">
                <div className="flex items-center space-x-2.5 text-2xl">
                  <History />
                  <p className="font-bold">{t('admin.history.company.title')}</p>
                </div>

                <p className="text-muted-foreground">{t('admin.history.company.description')}</p>

                <DataTable table={companyHistoryTable} />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="overflow-hidden rounded-xl border border-destructive/30 bg-destructive/5">
          <div className="flex gap-3 items-center px-6 py-4 bg-destructive">
            <AlertTriangle className="w-5 h-5 text-destructive-foreground shrink-0" />
            <h3 className="text-base font-semibold text-destructive-foreground">
              {t('admin.company.profile.danger.zone.title')}
            </h3>
          </div>
          <div className="flex flex-col gap-4 justify-between px-6 py-5 sm:flex-row sm:items-center">
            <p className="text-sm text-destructive/80">
              {t('admin.company.profile.danger.zone.description')}
            </p>
            <Button
              variant="destructive"
              rounded="full"
              className="shrink-0"
              onClick={() => setDeleteStep('warning')}
            >
              <Trash2 className="w-4 h-4" />
              {t('admin.company.profile.danger.zone.delete.button')}
            </Button>
          </div>
        </div>

        <Dialog
          open={deleteStep !== null}
          onOpenChange={(open) => {
            if (!open) closeDeleteModal();
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader className="items-center text-center">
              <div className="flex justify-center items-center mb-2 w-14 h-14 rounded-full bg-destructive/10">
                <Trash2 className="w-6 h-6 text-destructive" />
              </div>
              <DialogTitle className="text-center">
                {t('admin.company.delete.modal.title')}
              </DialogTitle>
            </DialogHeader>

            {deleteStep === 'warning' && (
              <>
                <div className="py-1 space-y-3">
                  <DialogDescription className="text-center">
                    {company.userWorkspaces.length > 0
                      ? t('admin.company.delete.modal.description.with.users')
                      : t('admin.company.delete.modal.description')}
                  </DialogDescription>
                  <p className="text-sm font-semibold text-center text-destructive">
                    {t('admin.company.delete.modal.irreversible')}
                  </p>
                </div>
                <DialogFooter className="flex-col-reverse gap-2 sm:flex-col sm:space-x-0">
                  <Button
                    variant="destructive"
                    rounded="full"
                    className="w-full"
                    onClick={() => setDeleteStep('confirm')}
                  >
                    {t('admin.company.delete.modal.next')}
                  </Button>
                  <Button
                    variant="outline"
                    rounded="full"
                    className="w-full"
                    onClick={closeDeleteModal}
                  >
                    {t('admin.company.delete.modal.cancel')}
                  </Button>
                </DialogFooter>
              </>
            )}

            {deleteStep === 'confirm' && (
              <>
                <div className="py-1 space-y-3">
                  <DialogDescription className="text-center">
                    {t('admin.company.delete.modal.confirm.description', { name: company.name })}
                  </DialogDescription>
                  <input
                    autoFocus
                    className="px-3 py-2 w-full text-sm rounded-md border border-input bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={company.name}
                    value={companyNameInput}
                    onChange={(e) => setCompanyNameInput(e.target.value)}
                  />
                </div>
                <DialogFooter className="flex-col-reverse gap-2 sm:flex-col sm:space-x-0">
                  <Button
                    variant="destructive"
                    rounded="full"
                    className="w-full"
                    loading={deleteMutation.isPending}
                    disabled={companyNameInput !== company.name}
                    onClick={() => deleteMutation.mutate({ id: companyId })}
                  >
                    {t('admin.company.delete.modal.confirm')}
                  </Button>
                  <Button
                    variant="outline"
                    rounded="full"
                    className="w-full"
                    disabled={deleteMutation.isPending}
                    onClick={() => setDeleteStep('warning')}
                  >
                    {t('admin.company.delete.modal.back')}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }
};
